import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import apiService from '../services/api';
import ApplicationSuccess from './ApplicationSuccess';
import { 
  Clock, 
  Home, 
  DollarSign, 
  Shield, 
  CheckCircle, 
  Users, 
  Building, 
  Briefcase, 
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  Star,
  Calculator,
  FileText,
  Wrench,
  UserCheck,
  CreditCard,
  FileX,
  ClipboardCheck,
  Scale,
  ChevronDown,
  BookOpen,
  Network,
  Building2
} from 'lucide-react';
import { mockData } from '../data/mock';
import SneakPeekSection from './SneakPeekSection';
import EnhancedMembershipApplication from './EnhancedMembershipApplication';
import { 
  PricingSection, 
  ComparisonSection,
  FooterSection 
} from './LandingPageSections';

const LandingPage = () => {
  const [calculatorData, setCalculatorData] = useState({
    doors: '',
    monthlyRent: ''
  });

  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    membershipLevel: '',
    managementStatus: '',
    businessName: '',
    licenseStatus: '',
    specialtySkills: [],
    otherSpecialty: '',
    agreeToTerms: false,
    consentDigitalSignature: false,
    acknowledgedPMA: false
  });

  const [showLoginDropdown, setShowLoginDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLoginDropdown && !event.target.closest('.portal-dropdown')) {
        setShowLoginDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLoginDropdown]);

  const [applicationState, setApplicationState] = useState({
    isSubmitting: false,
    isSuccess: false,
    applicationData: null,
    error: null
  });

  const calculateFees = () => {
    const doors = parseInt(calculatorData.doors);
    
    if (!doors) return null;
    
    if (doors < 12) {
      return "150"; // Standard membership
    } else if (doors >= 12 && doors <= 24) {
      return "300"; // Premium membership
    } else {
      // 25+ doors - custom pricing
      return "custom";
    }
  };

  const handleCalculatorChange = (field, value) => {
    setCalculatorData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field, value) => {
    // Handle phone number validation
    if (field === 'phone') {
      // Only allow digits, spaces, hyphens, parentheses, and plus sign
      const cleanValue = value.replace(/[^\d\s\-\(\)\+]/g, '');
      // Extract only digits to check length
      const digitsOnly = cleanValue.replace(/\D/g, '');
      
      // Limit to 10 digits maximum (US phone numbers)
      if (digitsOnly.length > 10) {
        // Take only the first 10 digits and preserve formatting
        const limitedDigits = digitsOnly.substring(0, 10);
        // Try to maintain some formatting based on original input
        let formattedValue = limitedDigits;
        if (limitedDigits.length >= 6) {
          formattedValue = `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
        } else if (limitedDigits.length >= 3) {
          formattedValue = `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
        }
        setContactForm(prev => ({ ...prev, [field]: formattedValue }));
        return;
      }
      
      setContactForm(prev => ({ ...prev, [field]: cleanValue }));
      return;
    }
    
    // Handle email validation
    if (field === 'email') {
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(value) || value === ''; // Allow empty for typing
      
      // Store email and validation status
      setContactForm(prev => ({ 
        ...prev, 
        [field]: value,
        emailValid: isValidEmail 
      }));
      return;
    }
    
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (applicationState.isSubmitting) return;
    
    setApplicationState(prev => ({...prev, isSubmitting: true, error: null}));
    
    try {
      // Generate username if not provided
      const fullName = `${contactForm.firstName || ''} ${contactForm.lastName || ''}`.trim();
      const username = contactForm.portalUsername || 
        fullName.toLowerCase().replace(/[^a-z]/g, '').substring(0, 15);
      
      // Clean phone number - remove all non-digit characters
      const cleanPhone = contactForm.phone.replace(/\D/g, '');
      
      // Prepare application data for backend
      const applicationData = {
        first_name: contactForm.firstName,
        last_name: contactForm.lastName,
        email: contactForm.email,
        phone: cleanPhone,
        membership_level: contactForm.membershipLevel,
        property_management_status: contactForm.managementStatus,
        business_name: contactForm.businessName || '',
        license_status: contactForm.licenseStatus || '',
        specialty_skills: contactForm.specialtySkills || [],
        other_specialty: contactForm.otherSpecialty || '',
        agree_to_terms: contactForm.agreeToTerms && contactForm.acknowledgedPMA,
        consent_digital_signature: contactForm.consentDigitalSignature || contactForm.agreeToTerms
      };
      
      console.log('Submitting application data:', applicationData);
      
      // Submit to backend
      const response = await apiService.submitMembershipApplication(applicationData);
      
      // Set success state
      setApplicationState(prev => ({
        ...prev,
        isSubmitting: false,
        isSuccess: true,
        applicationData: {
          applicationId: response.application_id,
          membershipLevel: contactForm.membershipLevel,
          portalUsername: username,
          nextSteps: response.next_steps,
          message: response.message
        }
      }));
      
    } catch (error) {
      console.error('Application submission failed:', error);
      
      // Ensure we always have a proper error message string
      let errorMessage = 'Failed to submit application. Please try again.';
      
      if (error && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (error && typeof error === 'string') {
        errorMessage = error;
      } else if (error && error.detail) {
        errorMessage = typeof error.detail === 'string' ? error.detail : 'Application submission failed.';
      }
      
      setApplicationState(prev => ({
        ...prev,
        isSubmitting: false,
        error: errorMessage
      }));
    }
  };
  
  const handleContinueFromSuccess = () => {
    setApplicationState(prev => ({...prev, isSuccess: false}));
    // Reset form for next application
    setContactForm({
      name: '', email: '', phone: '', properties: '', message: '',
      membershipLevel: '', managementStatus: '', specialtySkills: [], otherSpecialty: '',
      agreeToTerms: false, consentDigitalSignature: false,
      acknowledgedPMA: false
    });
  };

  const scrollToSection = (sectionId) => {
    console.log('Scrolling to section:', sectionId);
    const element = document.getElementById(sectionId);
    console.log('Element found:', element);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      console.log('Scroll initiated');
    } else {
      console.error('Element not found:', sectionId);
    }
  };

  // Show success page if application was submitted successfully
  if (applicationState.isSuccess) {
    return (
      <ApplicationSuccess 
        applicationData={applicationState.applicationData}
        onContinue={handleContinueFromSuccess}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-700">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-amber-400/30 shadow-xl">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg flex items-center justify-center shadow-lg">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white">Legacy Property Association</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-amber-100 hover:text-amber-300 transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('membership')} className="text-amber-100 hover:text-amber-300 transition-colors font-medium">Membership</button>
              <button onClick={() => scrollToSection('comparison')} className="text-amber-100 hover:text-amber-300 transition-colors font-medium">Compare</button>
              
              {/* Portal Login Dropdown */}
              <div className="relative portal-dropdown">
                <button 
                  onClick={() => setShowLoginDropdown(!showLoginDropdown)}
                  className="flex items-center space-x-1 text-amber-100 hover:text-amber-300 transition-colors font-medium"
                >
                  <span>Portal Login</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showLoginDropdown && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-amber-200 py-2 z-50">
                    {/* Basic Member Portal - Always Available */}
                    <button
                      onClick={() => {
                        window.location.href = '/basic-portal';
                        setShowLoginDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-amber-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">LPA Basic Member Portal</div>
                          <div className="text-xs text-slate-500">Basic members - Educational resources</div>
                        </div>
                      </div>
                    </button>

                    {/* Admin Portal */}
                    <button
                      onClick={() => {
                        window.location.href = '/admin';
                        setShowLoginDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors border-t border-slate-100"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <Shield className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">Admin Dashboard</div>
                          <div className="text-xs text-slate-500">For administrators only</div>
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </nav>

            <Button 
              onClick={() => {
                console.log('Apply for Membership button clicked');
                try {
                  // Check if user is already a Basic member
                  const basicMember = localStorage.getItem('basicMemberApplication');
                  console.log('Basic member check:', basicMember);
                  
                  if (basicMember) {
                    if (confirm('You already have Basic membership. Go to your Member Portal?')) {
                      window.location.href = '/basic-portal';
                    }
                  } else {
                    console.log('Scrolling to contact section');
                    scrollToSection('contact');
                  }
                } catch (error) {
                  console.error('Error in Apply for Membership button:', error);
                  // Fallback: scroll to contact section
                  scrollToSection('contact');
                }
              }} 
              className="bg-gradient-to-r from-blue-900 to-amber-600 hover:from-blue-800 hover:to-amber-700 text-white"
            >
              Apply for Membership
            </Button>
          </div>
        </div>
      </header>

      {/* Skills Opportunity Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 py-4 border-b-2 border-amber-400">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-center sm:text-left">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🔧</span>
              <span className="font-bold text-lg">Skilled Professionals:</span>
            </div>
            <div className="text-lg font-medium">
              Where skills meet opportunity - Connect with property owners who need your expertise
            </div>
            <Button 
              onClick={() => scrollToSection('membership')}
              className="bg-slate-900 hover:bg-slate-800 text-amber-400 font-semibold px-6 py-2 text-sm"
            >
              Join Network
            </Button>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-800/80 to-slate-900/90"></div>
        
        <div className="relative container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight font-serif">
                  <span className="text-white">Own Properties. </span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                    Live Free.
                  </span>
                </h1>
                <p className="text-xl text-white leading-relaxed max-w-2xl">
                  Tired of chasing rent, juggling spreadsheets, and handling tenant calls at midnight? The Legacy Property Association is a private membership for property owners and investors who are ready to upgrade from manual management to a streamlined digital solution that delivers freedom, ease, and flexibility.
                </p>
                <p className="text-lg text-amber-100 leading-relaxed max-w-2xl">
                  Our platform simplifies rent collection, maintenance requests, lease tracking, and compliance—all in one secure system. You keep the cash flow and control, while we remove the stress.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-900 to-amber-600 hover:from-blue-800 hover:to-amber-700 text-white px-8 py-3"
                  onClick={() => scrollToSection('contact')}
                >
                  Apply for Membership
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-amber-400 text-amber-300 hover:bg-amber-400/10 backdrop-blur-sm px-8 py-3"
                  onClick={() => scrollToSection('membership')}
                >
                  View Membership Levels
                </Button>
              </div>
              
              <div className="text-sm text-slate-400">
                <p>* Private Member Association operating under private contract law</p>
                <p>* Not operating as licensed real estate brokerage or property manager in public commerce</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full bg-gradient-to-r from-amber-400/30 to-amber-600/30 rounded-2xl -z-10 blur-lg"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1758193431355-54df41421657?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="Luxury residential property" 
                    className="rounded-xl shadow-2xl border border-amber-400/20"
                  />
                  <img 
                    src="https://images.unsplash.com/photo-1529290130-4ca3753253ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="Premium interior design" 
                    className="rounded-xl shadow-2xl border border-amber-400/20"
                  />
                </div>
                <div className="mt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="Elite property management" 
                    className="rounded-xl shadow-2xl border border-amber-400/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 font-serif">
              Why Join Legacy Property Association?
            </h2>
            <p className="text-xl text-amber-100 max-w-3xl mx-auto">
              Private membership benefits designed to support property owners while preserving your rights and autonomy.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.benefits.map((benefit, index) => (
              <Card key={index} className="text-center group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-white">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <benefit.icon className="w-8 h-8 text-slate-900" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2 font-serif">{benefit.title}</h3>
                  <p className="text-slate-700">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4 font-serif">
              Member Benefits & Services
            </h2>
            <p className="text-xl text-slate-600">
              Administrative coordination and educational resources exclusively for private members
            </p>
          </div>

          {/* Service Comparison Table */}
          <Card className="mb-16 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 border-b border-amber-400/20">
              <CardTitle className="text-center text-2xl text-white font-serif">Membership Benefits</CardTitle>
              <CardDescription className="text-center text-amber-200">Compare our membership levels and exclusive member benefits</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-amber-100 to-amber-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-slate-800">Benefit</th>
                      <th className="text-center py-4 px-6 font-semibold text-slate-800">Basic (Free)</th>
                      <th className="text-center py-4 px-6 font-semibold text-slate-800">Standard ($50)</th>
                      <th className="text-center py-4 px-6 font-semibold text-slate-800">Premium ($100)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockData.services.map((service, index) => {
                      const isBasicFeature = service.name === 'Educational Resources & Legal Updates' || service.name === 'General Contractor & Skilled Laborers Network';
                      const isStandardFeature = !service.isPremiumOnly && service.name !== 'Educational Resources & Legal Updates';
                      const isPremiumFeature = true; // Premium includes everything
                      
                      return (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-4 px-6">
                            <div className="flex items-start space-x-3">
                              <service.icon className="w-5 h-5 text-blue-900 mt-1" />
                              <div className="flex flex-col">
                                <span className={`font-medium ${
                                  service.name === 'Member Portal Access' ? 'text-amber-700 font-semibold' : 
                                  service.isHighlighted ? 'text-green-700 font-semibold' : ''
                                }`}>
                                  {service.name}
                                  {service.name === 'Member Portal Access' && <span className="text-xs text-amber-600 ml-1">🔑</span>}
                                  {service.isHighlighted && <span className="text-xs text-green-600 ml-1">⭐ NEW</span>}
                                </span>
                                {service.isPremiumOnly && (
                                  <span className="text-xs text-purple-600 font-semibold mt-1">
                                    👑 PREMIUM
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-4 px-6">
                            {isBasicFeature ? <CheckCircle className="w-5 h-5 text-green-600 mx-auto" /> : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="text-center py-4 px-6">
                            {(isBasicFeature || isStandardFeature) ? <CheckCircle className="w-5 h-5 text-green-600 mx-auto" /> : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="text-center py-4 px-6">
                            <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-blue-900 transition-colors">
                      <service.icon className="w-5 h-5 text-blue-900 group-hover:text-white" />
                    </div>
                    <CardTitle className="text-lg">{service.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <PricingSection 
        scrollToSection={scrollToSection}
        handleContactChange={handleContactChange}
      />
      <SneakPeekSection scrollToSection={scrollToSection} />
      {/* Who We Serve section removed */}
      {/* Member Success Stories moved to footer link */}
      <ComparisonSection />
      <EnhancedMembershipApplication 
        contactForm={contactForm}
        handleContactChange={handleContactChange}
        handleContactSubmit={handleContactSubmit}
        applicationState={applicationState}
      />
      <FooterSection scrollToSection={scrollToSection} />
    </div>
  );
};

export default LandingPage;